import { useState } from "react";
import { PlusIcon, PrinterIcon, WrenchScrewdriverIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

import {
  IVehicle,
  PartsPriority,
  PartsStatusDropdowns,
  PartsStatusValues,
  VehicleComplexity,
  YardStatus,
} from "../../models/Vehicle";
import useLocations from "../../data/useLocations";
import useVehiclesMutations from "../../data/useVehiclesMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { useLabelGenerator } from "../../hooks/useLabelGenerator";
import printHtml from "../../utils/printHtml";
import OnPrimaryButton from "../shared/Inputs/OnPrimaryButton";
import OnPrimarySelect from "../shared/Inputs/OnPrimarySelect";
import OnPrimaryComboBox from "../shared/Inputs/OnPrimaryComboBox";
import { SearchPaletteItem } from "../shared/Inputs/SearchPaletteTypes";
import BulkAddToWorkOrderModal from "../workOrders/BulkAddToWorkOrderModal";
import useNotification from "../notifications/useNotifications";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";

// Build {id, name} option lists from an enum, dropping its reverse numeric keys.
// The id stays the raw numeric enum index; the name is translated for display.
const enumOptions = (e: object, t: (key: string) => string) =>
  Object.values(e)
    .filter((v) => isNaN(Number(v)))
    .map((name, i) => ({ id: i, name: t(name as string) }));

export default function VehicleQuickActions({
  vehicle,
  onCreateDismantleJob,
}: {
  vehicle: IVehicle;
  /** Switches the vehicle page to the Parts tab where dismantle jobs are created. */
  onCreateDismantleJob?: () => void;
}) {
  const { t } = useTranslation("vehicles");
  const [workOrderOpen, setWorkOrderOpen] = useState(false);
  const [isPrintingVehicleLabel, setIsPrintingVehicleLabel] = useState(false);
  const isTme = CheckFeatureFlag("TME");

  const { locations } = useLocations();
  const { patchVehicle } = useVehiclesMutations();
  const { generateVehicleLabel } = useLabelGenerator();
  const { addNotification } = useNotification();
  const { saveHandlers: partsStatusHandlers } = useDefaultCRUDHandlers(t("Parts Status"));
  const { saveHandlers: partsPriorityHandlers } = useDefaultCRUDHandlers(t("Priority Status"));
  const { saveHandlers: locationHandlers } = useDefaultCRUDHandlers(t("Vehicle Location"));
  const { saveHandlers: yardStatusHandlers } = useDefaultCRUDHandlers(t("Yard Status"));
  const { saveHandlers: complexityHandlers } = useDefaultCRUDHandlers(t("Vehicle Complexity"));

  const patch = (changes: object, handlers: { onSuccess: () => void; onError: () => void }) =>
    patchVehicle.mutate(
      { vehicleId: vehicle.id, patch: changes },
      { onSuccess: () => handlers.onSuccess(), onError: () => handlers.onError() }
    );

  // Parts Status options come from PartsStatusDropdowns but the value persisted on
  // the vehicle is a PartsStatusValues, so we map between them by display name.
  const partsStatusOptions = Object.values(PartsStatusDropdowns)
    .filter((v) => isNaN(Number(v)))
    .map((name) => ({ id: name as string, name: t(name as string) }));

  const locationOptions = [{ id: "", name: t("No Location") }, ...(locations.data?.data ?? [])];

  const handleLocationUpdate = (locationId: string) => {
    const next = locationId || null;
    if (vehicle.locationId === next) return;
    patch({ locationId: next, updateLocationId: true }, locationHandlers);
  };

  const handlePrintVehicleLabel = async () => {
    setIsPrintingVehicleLabel(true);
    try {
      const html = await generateVehicleLabel(vehicle);
      printHtml(html);
    } catch {
      addNotification({ variant: "error", primaryText: t("Unable to print vehicle label") });
    } finally {
      setIsPrintingVehicleLabel(false);
    }
  };

  const linkedName = `${vehicle.vrm ?? t("Unknown VRM")}${vehicle.combinedMake ? ` - ${vehicle.combinedMake}` : ""}${vehicle.combinedModel ? ` ${vehicle.combinedModel}` : ""
    }`;

  const linkedItems: SearchPaletteItem[] = [{ id: vehicle.id, name: linkedName, type: "Vehicle" }];

  return (
    <div className="rounded-lg bg-primary-600 p-4 shadow">
      <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-white/70">{t("Quick Actions")}</h2>

      <BulkAddToWorkOrderModal
        open={workOrderOpen}
        onClose={() => setWorkOrderOpen(false)}
        selectedItems={linkedItems}
      />
      
      <div className="space-y-3">
        <OnPrimaryButton
          label={t("Create Dismantle Job")}
          variant="solid"
          icon={<WrenchScrewdriverIcon className="h-4 w-4" />}
          onClick={() => onCreateDismantleJob?.()}
          disabled={!onCreateDismantleJob}
        />

        <OnPrimaryButton
          label={t("Add to work order")}
          variant="outline"
          icon={<PlusIcon className="h-4 w-4" />}
          onClick={() => setWorkOrderOpen(true)}
        />

        <OnPrimaryButton
          label={t("Print Vehicle Labels")}
          variant="outline"
          icon={<PrinterIcon className="h-4 w-4" />}
          onClick={handlePrintVehicleLabel}
          isLoading={isPrintingVehicleLabel}
        />

        {!isTme && (
          <OnPrimarySelect
            label={t("Parts Status")}
            options={partsStatusOptions}
            value={PartsStatusValues[vehicle.engineExportStatus]}
            onChange={(name) => patch({ engineExportStatus: PartsStatusValues[name] }, partsStatusHandlers)}
            disabled={patchVehicle.isLoading}
          />
        )}
        {!isTme && (
          <OnPrimarySelect
            label={t("Parts Priority")}
            options={enumOptions(PartsPriority, t)}
            value={vehicle.partsPriority}
            onChange={(value) => patch({ partsPriority: value }, partsPriorityHandlers)}
            disabled={patchVehicle.isLoading}
          />
        )}

        <OnPrimaryComboBox
          key={`vehicle-location-${vehicle.id}-${vehicle.locationId ?? "none"}`}
          label={t("Yard Location")}
          options={locationOptions}
          value={vehicle.locationId ?? ""}
          onChange={handleLocationUpdate}
          placeholder={t("No Location")}
          disabled={locations.isLoading || patchVehicle.isLoading}
        />

        {!isTme && (
          <OnPrimarySelect
            label={t("Yard Status")}
            options={enumOptions(YardStatus, t)}
            value={vehicle.yardStatus}
            onChange={(value) => patch({ yardStatus: value }, yardStatusHandlers)}
            disabled={patchVehicle.isLoading}
          />
        )}

        <OnPrimarySelect
          label={t("Complexity")}
          options={enumOptions(VehicleComplexity, t)}
          value={vehicle.complexity}
          onChange={(value) => patch({ complexity: value }, complexityHandlers)}
          disabled={patchVehicle.isLoading}
        />


      </div>
    </div>
  );
}
